import {
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Tooltip,
  Box,
  Button,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { Api } from "../lib/api";
import PersonIcon from "@material-ui/icons/Person";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import AlarmOffIcon from "@material-ui/icons/AlarmOff";
import BlockIcon from "@material-ui/icons/Block";
import SyncIcon from "@material-ui/icons/Sync";
import DeleteIcon from "@material-ui/icons/Delete";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import PostAddIcon from "@material-ui/icons/PostAdd";
import {
  IconStyles,
  ModalStyles,
  StyledTableCell,
  TableColumnstyles,
} from "../styles";
import {
  UserStage,
  AdminDeactivateUserBodyParams,
  AdminSpamOverrideBodyParams,
  AdminUserDetailsResponse,
  PaymentType,
  SpamOverride,
  SubscriptionStatus,
  IdProvider,
} from "@authory-packages/core";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Routes } from "../routes";
import { addDays, format, formatDistance } from "date-fns";
import { getPaymentLabel } from "../utils/getPaymentLabel";
import { getPlanLabel } from "../utils/getPlanLabel";
import { SubscriptionPlanTooltip } from "./tooltips/SubscriptionPlanTooltip";
import { PaymentMethodTooltip } from "./tooltips/SubscriptionStatusTooltip";
import { showSignupDateCategory } from "../pages/UserList";

type UserDetailsSummaryProps = {
  userDetails?: AdminUserDetailsResponse;
  userSlug?: string;
  onBulkImportClick: () => void;
  onCreateSourceClick: () => void;
  onUserChangeStatus: () => void;
};

const deactivateUserAllowedStages = [
  UserStage.Active,
  UserStage.ActiveImportFinished,
  UserStage.ActiveAwaitingFeedback,
  UserStage.ActiveNewWebsite,
  UserStage.ActiveFullCrawlRunning,
  UserStage.NewSignup,
  UserStage.CompletedSignup,
  UserStage.FullCrawlRunning,
  UserStage.MarkedForDowngrade,
];

const deactivateEndOfBillingPeriodUserAllowedStages = [
  SubscriptionStatus.Future,
  SubscriptionStatus.Active,
];

const startCrawlAllowedStages = [
  UserStage.CompletedSignup,
  UserStage.NewSignup,
];

const endOfBillingDeactivationAllowedPayments = [
  PaymentType.ChargebeePaypal,
  PaymentType.ChargebeeStripe,
  PaymentType.ChargebeeApplePay,
  PaymentType.ChargebeeGooglePay,
];

// Automatic sends null, the spam check decides
const spamOverrideOptions: {
  value: string;
  label: string;
  override: SpamOverride | null;
}[] = [
  { value: "Automatic", label: "Automatic", override: null },
  { value: SpamOverride.Spam, label: "Spam", override: SpamOverride.Spam },
  {
    value: SpamOverride.NotSpam,
    label: "Not spam",
    override: SpamOverride.NotSpam,
  },
];

const getShownDate = (user: AdminUserDetailsResponse) => {
  if (user.signupCompletedDate)
    return format(new Date(user.signupCompletedDate), "dd.MM.yyy HH:mm:ss");
  return "-";
};

export const UserDetailsSummary = ({
  userDetails,
  userSlug,
  onBulkImportClick,
  onCreateSourceClick,
  onUserChangeStatus,
}: UserDetailsSummaryProps) => {
  const iconClasses = IconStyles();
  const modalStyles = ModalStyles();
  const tableColumnStyles = TableColumnstyles();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [deactivateImmediately, setDeactivateImediately] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const showSignupDate = userDetails?.stage
    ? showSignupDateCategory.includes(userDetails?.stage)
    : false;

  const allowedCancelEndOfBillingStates =
    userDetails &&
    userDetails.subscriptionStatus &&
    deactivateEndOfBillingPeriodUserAllowedStages.includes(
      userDetails.subscriptionStatus
    );
  const isTrialCancelled =
    userDetails && userDetails.subscriptionStatus === SubscriptionStatus.InTrial
      ? userDetails?.isSubscriptionCancelled
      : true;

  const allowedCancelEndBilling =
    allowedCancelEndOfBillingStates || !isTrialCancelled;

  const spamOverrideValue =
    spamOverrideOptions.find(
      (option) => option.override === (userDetails?.spamOverride ?? null)
    )?.value ?? "Automatic";

  const onSpamOverrideChange = async (value: string) => {
    const option = spamOverrideOptions.find((o) => o.value === value);
    if (!userSlug || !option) return;

    try {
      const requestParams = new AdminSpamOverrideBodyParams();
      requestParams.override = option.override;

      await Api.setSpamOverride(userSlug, requestParams);
      onUserChangeStatus();
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message, {
        variant: "error",
      });
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        <Typography variant="h6" color="primary">
          Summary{" "}
          {userDetails && (
            <>
              - Flagged as Spam: {userDetails.flaggedAsSpam ? "Yes" : "No"} -
              Awaiting Spam Check:{" "}
              {userDetails.awaitingSpamCheck ? "Yes" : "No"}{" "}
            </>
          )}
          {showSignupDate && (
            <>
              - Onboarding Time Left:{" "}
              {userDetails?.signupCompletedDate
                ? formatDistance(
                    addDays(new Date(userDetails.signupCompletedDate), 2),
                    new Date(),
                    { addSuffix: true }
                  )
                : "<Signup not finished>"}
            </>
          )}
        </Typography>
        {userDetails && (
          <Box ml={2}>
            <FormControl size="small" style={{ minWidth: 140 }}>
              <InputLabel>Spam override</InputLabel>
              <Select
                value={spamOverrideValue}
                onChange={(e) => onSpamOverrideChange(String(e.target.value))}
              >
                {spamOverrideOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell style={{ paddingLeft: 16 }}>
                Slug
              </StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell align="center">Login Method</StyledTableCell>
              <StyledTableCell align="center">Signup Date</StyledTableCell>
              <StyledTableCell align="center">Nº Sources</StyledTableCell>
              <StyledTableCell align="center">
                <PaymentMethodTooltip>
                  <div>Payment</div>
                </PaymentMethodTooltip>
              </StyledTableCell>
              <StyledTableCell align="center">
                <SubscriptionPlanTooltip>
                  <div>Subscription Plan</div>
                </SubscriptionPlanTooltip>
              </StyledTableCell>
              <StyledTableCell align="center">
                Subscription Status
              </StyledTableCell>
              <StyledTableCell align="center">Account Type</StyledTableCell>
              <StyledTableCell align="center">Id</StyledTableCell>
              <StyledTableCell style={{ minWidth: 200 }} align="center">
                Actions
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <StyledTableCell
                style={{ paddingLeft: 16 }}
                component="td"
                scope="row"
              >
                {userDetails?.slug}
              </StyledTableCell>
              <StyledTableCell component="td" scope="row">
                {`${userDetails?.firstName} ${userDetails?.lastName}`}
              </StyledTableCell>
              <StyledTableCell
                component="td"
                scope="row"
                style={{ wordBreak: "break-all" }}
              >
                {userDetails?.email}
              </StyledTableCell>
              <StyledTableCell
                component="td"
                scope="row"
                style={{ wordBreak: "break-all" }}
              >
                {userDetails?.loginMethod}
              </StyledTableCell>
              <StyledTableCell align="center" component="td" scope="row">
                {userDetails && getShownDate(userDetails)}
              </StyledTableCell>
              <StyledTableCell align="center" component="td" scope="row">
                {userDetails?.websiteCount}
              </StyledTableCell>
              <StyledTableCell
                align="center"
                component="td"
                scope="row"
                style={{ wordBreak: "break-all" }}
              >
                {userDetails?.paymentType &&
                  getPaymentLabel(userDetails.paymentType)}
              </StyledTableCell>
              <StyledTableCell align="center" component="td" scope="row">
                {getPlanLabel(userDetails?.subscriptionPlanKey)}
              </StyledTableCell>
              <StyledTableCell align="center" component="td" scope="row">
                {userDetails?.subscriptionStatus}{" "}
                {userDetails?.isSubscriptionCancelled &&
                userDetails?.stage !== UserStage.Inactive
                  ? "(Cancel Scheduled)"
                  : ""}
              </StyledTableCell>
              <StyledTableCell align="center" component="td" scope="row">
                {userDetails?.accountType}
              </StyledTableCell>
              <StyledTableCell align="center" component="td" scope="row">
                {IdProvider.toUserId(userDetails?.id || 0)}
              </StyledTableCell>
              <StyledTableCell
                align="center"
                component="td"
                scope="row"
                className={tableColumnStyles.actionsSpaceLastItem}
              >
                <Tooltip title="Impersonate - God mode">
                  <PersonIcon
                    className={iconClasses.root}
                    color="action"
                    onClick={async () => {
                      if (!userSlug) return;

                      const impersonateRes = await Api.impersonate(userSlug);
                      await window.open(
                        impersonateRes.data.redirectTo,
                        "_blank"
                      );
                    }}
                  />
                </Tooltip>
                <Tooltip title="Bulk upload - auto source selection">
                  <CloudUploadIcon
                    className={iconClasses.root}
                    color="action"
                    onClick={onBulkImportClick}
                  />
                </Tooltip>
                <Tooltip title="Add new source">
                  <PostAddIcon
                    className={iconClasses.root}
                    color="action"
                    onClick={onCreateSourceClick}
                  />
                </Tooltip>
                {(userDetails?.stage === UserStage.ImportFinished ||
                  userDetails?.stage === UserStage.FullCrawlRunning) && (
                  <Tooltip title="Activate User">
                    <AssignmentTurnedInIcon
                      className={iconClasses.root}
                      color="action"
                      onClick={async () => {
                        try {
                          if (!userSlug) return;

                          await Api.activateUser(userSlug);
                          onUserChangeStatus();
                        } catch (error: any) {
                          enqueueSnackbar(error.response.data.message, {
                            variant: "error",
                          });
                        }
                      }}
                    />
                  </Tooltip>
                )}
                {userDetails?.stage &&
                  startCrawlAllowedStages.includes(userDetails?.stage) && (
                    <Tooltip title="Start Crawl">
                      <PlayArrowIcon
                        className={iconClasses.root}
                        color="action"
                        onClick={async () => {
                          try {
                            if (!userSlug) return;

                            await Api.startCrawlUser(userSlug);
                            onUserChangeStatus();
                          } catch (error: any) {
                            enqueueSnackbar(error.response.data.message, {
                              variant: "error",
                            });
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                {userDetails?.stage === UserStage.Inactive && (
                  <>
                    <Tooltip title="Reactivate User">
                      <SyncIcon
                        className={iconClasses.root}
                        color="action"
                        onClick={async () => {
                          try {
                            if (!userSlug) return;

                            await Api.reactivateUser(userSlug);
                            onUserChangeStatus();
                          } catch (error: any) {
                            enqueueSnackbar(error.response.data.message, {
                              variant: "error",
                            });
                          }
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Delete User forever">
                      <DeleteIcon
                        className={iconClasses.root}
                        color="action"
                        onClick={() => setDeleteModalOpen(true)}
                      />
                    </Tooltip>
                  </>
                )}
                {userDetails &&
                  endOfBillingDeactivationAllowedPayments.includes(
                    userDetails?.paymentType
                  ) &&
                  userDetails?.stage &&
                  deactivateUserAllowedStages.includes(userDetails.stage) &&
                  allowedCancelEndBilling && (
                    <Tooltip title="Deactivate at end of billing period User">
                      <AlarmOffIcon
                        className={iconClasses.root}
                        color="action"
                        onClick={() => {
                          setDeactivateImediately(false);
                          setDeactivateModalOpen(true);
                        }}
                      />
                    </Tooltip>
                  )}
                {userDetails?.stage &&
                  deactivateUserAllowedStages.includes(userDetails?.stage) && (
                    <Tooltip title="Deactivate and Archive User">
                      <BlockIcon
                        className={iconClasses.root}
                        color="action"
                        onClick={() => {
                          setDeactivateImediately(true);
                          setDeactivateModalOpen(true);
                        }}
                      />
                    </Tooltip>
                  )}
              </StyledTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <div className={modalStyles.paper}>
          <>
            <Box mb={2}>
              <Typography variant="h4" component="h4">
                {` Delete User  ${userSlug}`}
              </Typography>
            </Box>
            <Box mb={6}>
              <Typography>
                {`Please confirm you want to delete user ${userDetails?.firstName} ${userDetails?.lastName} - ${userSlug}!!!`}
              </Typography>
            </Box>
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={() => setDeleteModalOpen(false)}
                style={{ marginRight: 20 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={async () => {
                  if (!userSlug) return;

                  try {
                    await Api.deleteUser(userSlug);
                    window.location.href = Routes.InactiveUser;
                  } catch (error: any) {
                    enqueueSnackbar(error.response.data.message, {
                      variant: "error",
                    });
                  }
                }}
              >
                Delete
              </Button>
            </Box>
          </>
        </div>
      </Modal>
      <Modal
        open={deactivateModalOpen}
        onClose={() => setDeactivateModalOpen(false)}
      >
        <div className={modalStyles.paper}>
          <>
            <Box mb={2}>
              <Typography variant="h4" component="h4">
                {`Deactivate User  ${userSlug}`}
              </Typography>
            </Box>
            <Box mb={6}>
              <Typography>
                {`Please confirm you want to ${
                  deactivateImmediately
                    ? "immediately deactivate user"
                    : "deactive at the end of billing period user"
                }  ${userDetails?.firstName} ${
                  userDetails?.lastName
                } - ${userSlug}!!!`}
              </Typography>
            </Box>
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={() => setDeactivateModalOpen(false)}
                style={{ marginRight: 20 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={async () => {
                  if (!userSlug) return;

                  try {
                    if (!userSlug) return;

                    const requestParams = new AdminDeactivateUserBodyParams();
                    requestParams.cancelImmediately = deactivateImmediately;

                    await Api.deactivateUser(userSlug, requestParams);
                    onUserChangeStatus();
                    setDeactivateModalOpen(false);
                  } catch (error: any) {
                    enqueueSnackbar(error.response.data.message, {
                      variant: "error",
                    });
                  }
                }}
              >
                Deactivate
              </Button>
            </Box>
          </>
        </div>
      </Modal>
    </>
  );
};
